import { useEffect, useState } from 'react';
import Helmet from 'src/common/Helmet';
import { ForbidViewAsTaxPro } from 'src/components/Rebrand/QuebecDemo/ForbidViewAsTaxPro';
import { getShowInvitesValue } from 'src/components/Rebrand/QuebecDemo/IncomingInvites/getShowInvites';
import { InviteTaxProClientModal } from 'src/components/Rebrand/QuebecDemo/SendInviteModal/InviteTaxProClientModal';
import { TaxProClientsView } from 'src/components/Rebrand/QuebecDemo/TaxProClients';

import {
  H5,
  PageContainer,
  PageHeader,
  PageTitle,
} from '@cointracker/styleguide';
import {
  ConfirmModal,
  ConfirmModalProvider,
} from '@cointracker/styleguide/src/ConfirmModal';
import { Body2, Button, Heading } from '@cointracker/styleguide/src/LoggedIn';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@cointracker/styleguide/src/LoggedIn/Tabs';
import { CaretDown } from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { CSVImportModal } from '../../../../components/Rebrand/QuebecDemo/CSVImportModal';
import {
  TaxCaseProvider,
  TaxPayerCase,
  useTaxCaseStore,
} from '../../../../components/Rebrand/QuebecDemo/TaxProClients/context';

const CLIENTS = 'CLIENTS';
const INVITES = 'INVITES';

// Clean Spinner Loading State
const PageLoadingState = () => {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background-default">
      <div className="flex flex-col items-center gap-24">
        {/* Main Spinner */}
        <div className="relative">
          {/* Outer Ring */}
          <div className="h-80 w-80 rounded-full border-4 border-accent-subtle-blue opacity-20"></div>

          {/* Middle Ring */}
          <div className="absolute inset-8 h-64 w-64 animate-spin rounded-full border-4 border-accent-bold-blue border-t-transparent"></div>

          {/* Inner Ring */}
          <div
            className="border-3 absolute inset-16 h-48 w-48 animate-spin rounded-full border-accent-bold-blue border-b-transparent"
            style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}
          ></div>

          {/* Center Dot */}
          <div className="absolute inset-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full bg-accent-bold-blue"></div>
        </div>

        {/* Loading Text */}
        <div className="text-center">
          <Heading variant="h5" className="mb-8 text-text-primary">
            Loading Cases
          </Heading>
          <Body2 className="text-text-secondary">
            Please wait while we fetch your tax cases...
          </Body2>
        </div>
      </div>
    </div>
  );
};

const TaxYearDropdown = ({
  taxYears,
  selectedYear,
  onSelectYear,
}: {
  taxYears: number[];
  selectedYear: number | null;
  onSelectYear: (year: number | null) => void;
}) => {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="flex items-center gap-2 px-12 py-6">
          <Body2>
            {selectedYear ? `Tax Year ${selectedYear}` : 'All Tax Years'}
          </Body2>
          <CaretDown size={16} weight="bold" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 min-w-[180px] rounded-8 border border-line-tertiary bg-background-default p-4 shadow-md"
          sideOffset={5}
        >
          <DropdownMenu.Item
            className="text-sm hover:bg-background-secondary cursor-pointer rounded-4 px-8 py-6 focus:outline-none"
            onSelect={() => onSelectYear(null)}
          >
            All Tax Years
          </DropdownMenu.Item>

          {taxYears.map((year) => (
            <DropdownMenu.Item
              key={year}
              className="text-sm hover:bg-background-secondary cursor-pointer rounded-4 px-8 py-6 focus:outline-none"
              onSelect={() => onSelectYear(year)}
            >
              Tax Year {year}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

const TaxProView = () => {
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showCSVImportDialog, setShowCSVImportDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const defaultActiveKey = getShowInvitesValue() ? INVITES : CLIENTS;
  const { state, dispatch } = useTaxCaseStore();
  const [selectedTaxYear, setSelectedTaxYear] = useState<number | null>(null);

  const taxYears = Array.from(
    new Set(
      state.cases
        .flatMap((caseItem) => caseItem.taxYears)
        .filter((year): year is number => year !== null && year !== undefined),
    ),
  ).sort((a, b) => b - a);

  useEffect(() => {
    localStorage.removeItem('quebec_demo_viewing_as');
    localStorage.removeItem('spain_demo_viewing_as');
    localStorage.removeItem('gov_demo_viewing_as');

    // Simulate loading time for demo purposes
    const loadingTimer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(loadingTimer);
  }, []);

  const handleImportCases = (importedCases: TaxPayerCase[]) => {
    importedCases.forEach((caseData) => {
      dispatch({
        type: 'ADD_CASE',
        payload: caseData,
      });
    });
  };

  // Show loading state
  if (isLoading) {
    return <PageLoadingState />;
  }

  return (
    <>
      <ConfirmModalProvider>
        <ConfirmModal />
        <InviteTaxProClientModal
          show={showInviteDialog}
          onHide={() => setShowInviteDialog(false)}
        />
        <CSVImportModal
          isOpen={showCSVImportDialog}
          onDismiss={() => setShowCSVImportDialog(false)}
          onImport={handleImportCases}
        />
        <PageContainer>
          <PageHeader>
            <PageTitle className="flex flex-1 flex-row items-center gap-8">
              <H5>Cases</H5>{' '}
              <TaxYearDropdown
                taxYears={taxYears}
                selectedYear={selectedTaxYear}
                onSelectYear={setSelectedTaxYear}
              />
            </PageTitle>
            <div className="flex flex-row items-center gap-8">
              <Button
                onClick={() => setShowCSVImportDialog(true)}
                variant="primary"
              >
                Bulk Import Cases
              </Button>
              <Button
                onClick={() => setShowInviteDialog(true)}
                variant="primary"
              >
                Add Case
              </Button>
            </div>
          </PageHeader>
          <div className="flex flex-col gap-24">
            <Tabs defaultValue={defaultActiveKey}>
              <TabsList>
                <TabsTrigger value={CLIENTS}>Active</TabsTrigger>
                <TabsTrigger value={INVITES}>Closed</TabsTrigger>
              </TabsList>
              <TabsContent value={CLIENTS} className="mt-24">
                <TaxProClientsView mode="active" taxYear={selectedTaxYear} />
              </TabsContent>
              <TabsContent value={INVITES} className="mt-24">
                <TaxProClientsView mode="closed" taxYear={selectedTaxYear} />
              </TabsContent>
            </Tabs>
          </div>
        </PageContainer>
      </ConfirmModalProvider>
    </>
  );
};

const TaxProViewWithContext = () => {
  return (
    <TaxCaseProvider>
      <TaxProView />
    </TaxCaseProvider>
  );
};

export default function TaxProViewPageSpa() {
  return (
    <>
      <Helmet title="View Tax Pro" />
      <ForbidViewAsTaxPro>
        <TaxProViewWithContext />
      </ForbidViewAsTaxPro>
    </>
  );
}
